













































import Vue from 'vue';
import Component from 'vue-class-component';

// Store types
import {
    Mutation,
    CompanyAction,
    SpacesAction,
    SystemsAction,
    ProductsAction,
    RequestAction,
    SpacesGetter,
    RequestMutation,
} from '@/store/store.types';

// Partial components
import ProgressBar from '@/components/partials/ProgressBar.vue';
import Footer from '@/components/partials/Footer.vue';

// Used for the loading error
import AlertModal from '@/components/modal/Alert.vue';
import { InteractiveModalOptions } from '@/components/modal/types';

// Steps
import { Step } from '@/steps/types';

// Api
import Api from '@/api';

/**
 *  Coldtec Customer Offer Software tool.
 */
@Component({
    components: {
        ProgressBar,
        Footer,
    },
})
export default class CCOSTool extends Vue {

    public $refs!: {
        alert: AlertModal;
    };

    /**
     *  Holding the general error state of the tool.
     */
    private error = false;

    /**
     *
     *  Used to determine if the main layout partials should be rendered.
     *
     *  (i) Including the progress bar, steps and footer
     *
     */
    private get showLayout(): boolean {

        return !this.$store.state.loading && !this.error;

    }

    /**
     *
     *  Used to determine if the progress should be hidden.
     *
     *  (i) Allowing every step to hide the progress if needed,
     *  just by setting the `hideProgress` prop
     *
     */
    private get hideProgress(): boolean {

        return this.$route.meta && this.$route.meta.hideProgress;

    }

    /**
     *  Used to get the fact if the footer is showing info.
     */
    private get infoShown(): boolean {

        return this.$store.state.footer.info !== null;

    }

    /**
     *  Try to set the company guid for the API and fetch all
     *  needed data, including the company and data for
     *  configuring the spaces.
     */
    private async created(): Promise<void> {

        /**
         *
         *  Try to set the company guid and secret in the API client.
         *
         *  (i) If no or invalid company guid is provided, the following
         *  store dispatches fail and the tool shows the general error.
         *
         */
        this._setApiCompany();

        /**
         *  Attempt to fetch all pieces of data that are
         *  needed to construct an offer request.
         */
        try {

            /**
             *  Fetch and store the CSRF token that is
             *  required to make API proxy requests.
             */
            await Api.fetchCSRFToken();

            /**
             *  Fetch and store the company details.
             */
            await this.$store.dispatch(CompanyAction.FETCH_DETAILS);

            /**
             *  Fetch and store all the data that is needed to configure the spaces.
             */
            await this.$store.dispatch(SpacesAction.FETCH_CONFIG_DATA);

            /**
             *  Fetch and store all the available system filters.
             */
            await this.$store.dispatch(SystemsAction.FILTERS_FETCH);

            /**
             *  Fetch and store all the available products.
             */
            await this.$store.dispatch(ProductsAction.FETCH_ADDITIONAL);
            await this.$store.dispatch(ProductsAction.FETCH_SERVICE);

            /**
             *  Ensure the user always follows the happy
             *  path by starting at the intro.
             */
            void this._navigate(Step.Intro);

        } catch (_error) {

            void this._showError('Tijdens het laden is er helaas iets fout gegaan, waardoor de tool momenteel niet gebruikt kan worden. Neem contact met ons op via de contact pagina of probeer de tool te herladen.');

        }

        /**
         *
         *  Check if a request guid is provided and
         *  fetch and restore the details if so.
         *
         *  (i) Automatically navigates the user to
         *  the spaces step on success
         *
         */
        try {

            await this._fetchRequest();

            /**
             *  Programmatically navigate the user to the spaces
             *  step if a request was restored (spaces are set).
             */
            if (this.$store.getters[SpacesGetter.COUNT_ADDED]) {

                void this._navigate(Step.Spaces);

            }

        } catch (_error) {

            void this._showError('Tijdens het laden van de offerte aanvraag ging er helaas iets mis. Probeer het later nog eens of probeer de tool te herladen.');

        }

        /**
         *  Resolve the general loading state of the tool,
         *  hiding the loader and showing the tool.
         */
        this.$store.commit(Mutation.LOADING, false);

    }

    /**
     *  Used to get the company guid and secret
     *  from the route's query params and set it
     *  in the API client.
     */
    private _setApiCompany(): void {

        /**
         *  Store the company guid in the API's client.
         */
        Api.setCompanyGuid(this._getQuery('company') || '');

        /**
         *
         *  Try to get the secret from the queries and
         *  only try to store it if present.
         *
         *  (i) Because a secret is optional, while a company
         *  guid is mandatory for the tool to work
         *
         */
        const secret: string | undefined = this._getQuery('secret');

        if (!secret) {

            return;

        }

        Api.setCompanySecret(secret);

        /**
         *
         *  Store the internal state.
         *
         *  (i) If the secret seems invalid, the API throws
         *  an error and the tool show the general error.
         *
         */
        this.$store.commit(Mutation.INTERNAL, true);

    }

    /**
     *  Used to show the general error and allow
     *  users to reload the tool on confirm.
     */
    private async _showError(body: string): Promise<void> {

        this.error = true;

        /**
         *  Construct the options for the error alert.
         */
        const options: InteractiveModalOptions = {
            title: 'Sorry, er is iets fout gegaan',
            body,
            width: '35rem',
            confirm: {
                title: 'De tool herladen',
                content: 'Herladen',
            },
        };

        /**
         *  Reload the tool if the user hits confirm (reload).
         */
        if (await this.$refs.alert.open(options)) {

            location.reload();

        }

    }

    /**
     *
     *  Used to fetch and restore the details of a request.
     *
     *  (i) Gets the guid of the request from the `request` query
     *
     */
    private async _fetchRequest(): Promise<void> {

        const requestGuid: string | undefined = this._getQuery('request');

        if (!requestGuid) {

            return;

        }

        await this.$store.dispatch(RequestAction.FETCH, requestGuid);

        /**
         *  Store the guid of the request, so it can
         *  be used for the update HTTP request
         */
        this.$store.commit(RequestMutation.REQUEST_GUID, requestGuid);

    }

    /**
     *
     *  Used to get a query value from the URL.
     *
     *  @param key The key of the query to get the value of
     *
     *  @returns The value of the query or undefined
     *
     */
    private _getQuery(key: string): string | undefined {

        return Object.prototype.hasOwnProperty.call(this.$route.query, key) && typeof this.$route.query[key] === 'string'
            ? this.$route.query[key] as string
            : undefined;

    }

    /**
     *
     *  Used to navigate the tool to a specific step (if not already active).
     *
     *  @param step The step to navigate to
     *
     */
    private async _navigate(step: Step): Promise<void> {

        if (this.$route.name === step as string) {

            return;

        }

        await this.$router.push(step);

    }

}
